import React, { useEffect, useRef, useState } from 'react';
import { BookIcon, NewsIcon, TargetIcon, MessageCircleIcon, BriefcaseIcon, UsersIcon, HelpCircleIcon, UserIcon, BellIcon, DownloadIcon, CirclesIcon, FamilyIcon, SparkleIcon, BugIcon, MegaphoneIcon } from './Icons';
import styles from '../styles/HamburgerMenu.module.css';
import { usePWAInstall } from '../hooks/usePWAInstall';
import useFocusTrap from '../hooks/useFocusTrap';

// Money Dates is deprioritized — hidden from the menu. Flip to true to restore.
const SHOW_MONEY_DATES = false;
// News is not in use right now — hidden from the menu. Flip to true to restore.
const SHOW_NEWS = false;

/**
 * Hamburger Menu Component
 * Displays a right-side slide-in panel with navigation options
 *
 * Props:
 * - isOpen: Whether the menu is open
 * - onClose: Callback when menu should close
 * - onNavigateTasks: Navigate to tasks view
 * - onNavigateProgress: Navigate to progress dashboard view
 * - onNavigateBracelet: Navigate to achievement bracelet view
 * - onNavigateArticles: Navigate to all articles view
 * - onNavigateNews: Navigate to all news view
 * - onNavigateCoachDashboard: Navigate to coach dashboard (for coaches)
 * - onNavigateOrgDashboard: Navigate to organization dashboard (for org admins)
 * - onNavigateDevDashboard: Navigate to dev dashboard (development only)
 * - onNavigateCommunityHub: Navigate to the Community Manager Hub (community leaders)
 * - onFeedback: Open feedback modal
 * - onStartTour: Start the onboarding tour
 * - onNavigateSettings: Navigate to settings/profile screen
 * - user: User object with firstname, lastname, email, isCoach, adminOfOrganizations
 */
function HamburgerMenu({
  isOpen,
  onClose,
  onNavigateTasks,
  onNavigateProgress,
  onNavigateBracelet,
  onNavigateArticles,
  onNavigateNews,
  onNavigateMoneyDates,
  onNavigateCircles,
  onNavigateFamily,
  onNavigateCoachDashboard,
  onNavigateOrgDashboard,
  onNavigateDevDashboard,
  onNavigateCommunityHub,
  onFeedback,
  onStartTour,
  onNavigateSettings,
  user
}) {
  const panelRef = useRef(null);
  // Same keyboard contract as the Modal shell: focus moves into the drawer,
  // Tab cycles inside it, focus returns on close. Escape is handled by the
  // dedicated effect below (it must dismiss the iOS instructions first), so
  // the trap gets no onClose.
  useFocusTrap(panelRef, isOpen);
  const { isInstallable, promptInstall, isIOS } = usePWAInstall();
  const [notificationPermission, setNotificationPermission] = useState(
    'Notification' in window ? Notification.permission : 'unsupported'
  );
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  // Close on escape key. The iOS install instructions render as a modal on top
  // of the menu, so Escape dismisses that first and only closes the whole menu
  // once it is gone.
  useEffect(() => {
    function handleEscape(e) {
      if (e.key !== 'Escape' || !isOpen) return;
      if (showIOSInstructions) {
        setShowIOSInstructions(false);
        return;
      }
      onClose();
    }

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, showIOSInstructions]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Update notification permission when menu opens
      if ('Notification' in window) {
        setNotificationPermission(Notification.permission);
      }
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle notification permission request
  const handleEnableNotifications = async () => {
    console.log('[HamburgerMenu] Enable notifications clicked, current permission:', notificationPermission);

    if ('Notification' in window) {
      try {
        if (notificationPermission === 'granted') {
          // Show info that they need to disable in device settings
          console.log('[HamburgerMenu] Notifications already granted, showing alert');
          alert('To disable notifications, please go to your device/browser settings and turn off notifications for this app.');
        } else if (notificationPermission === 'denied') {
          // Permission was previously denied - show instructions to reset
          console.log('[HamburgerMenu] Notifications were previously blocked');
          alert('Notifications were blocked. To enable them:\n\n1. Click the lock icon (🔒) or info icon (ⓘ) in your browser\'s address bar\n2. Find "Notifications" in the permissions list\n3. Change it from "Block" to "Allow"\n4. Refresh the page');
        } else {
          // Request permission (permission is 'default')
          console.log('[HamburgerMenu] Requesting notification permission...');
          const permission = await Notification.requestPermission();
          console.log('[HamburgerMenu] Permission result:', permission);
          setNotificationPermission(permission);

          // If still denied after request, show the instructions
          if (permission === 'denied') {
            alert('Notifications were blocked. To enable them:\n\n1. Click the lock icon (🔒) or info icon (ⓘ) in your browser\'s address bar\n2. Find "Notifications" in the permissions list\n3. Change it from "Block" to "Allow"\n4. Refresh the page');
          }
        }
      } catch (error) {
        console.error('[HamburgerMenu] Error requesting notification permission:', error);
      }
    } else {
      console.log('[HamburgerMenu] Notifications not supported in this browser');
    }
  };

  // Handle PWA install
  const handleInstallPWA = async () => {
    if (isIOS) {
      // Show iOS instructions
      setShowIOSInstructions(true);
    } else if (isInstallable) {
      await promptInstall();
    }
  };

  if (!isOpen) return null;

  // Backdrops close on outside click only. Per ARIA authoring practice the
  // backdrop itself is not focusable — the keyboard path is Escape (above) plus
  // the labelled close button inside each panel.
  const handleIOSOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      setShowIOSInstructions(false);
    }
  };

  const handleMenuOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <>
      {/* iOS Install Instructions Modal */}
      {showIOSInstructions && (
        <div className={styles.menuOverlay} onClick={handleIOSOverlayClick}>
          <div
            className={styles.iosModal}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="ios-install-title"
          >
            <div className={styles.iosModalTitle} id="ios-install-title">Install Sophia on iOS</div>
            <div className={styles.iosInstructions}>
              <div className={styles.iosStep}>
                <div className={styles.iosStepNumber}>1</div>
                <div className={styles.iosStepText}>
                  Tap the <strong>Share</strong> button <span className={styles.iosIcon}>⎘</span> at the bottom of Safari
                </div>
              </div>
              <div className={styles.iosStep}>
                <div className={styles.iosStepNumber}>2</div>
                <div className={styles.iosStepText}>
                  Scroll down and tap <strong>"Add to Home Screen"</strong> <span className={styles.iosIcon}>➕</span>
                </div>
              </div>
              <div className={styles.iosStep}>
                <div className={styles.iosStepNumber}>3</div>
                <div className={styles.iosStepText}>
                  Tap <strong>"Add"</strong> in the top right corner
                </div>
              </div>
            </div>
            <div className={styles.iosModalButtons}>
              <button
                className={styles.iosModalBtn}
                onClick={() => setShowIOSInstructions(false)}
              >
                Got It
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={styles.menuOverlay} onClick={handleMenuOverlayClick}>
        <div
          ref={panelRef}
          className={styles.menuPanel}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="hamburger-menu-title"
        >
        <div className={styles.menuHeader}>
          <div>
            <h2 className={styles.menuTitle} id="hamburger-menu-title">Menu</h2>
            {user && (
              <div className={styles.userInfo}>
                <div className={styles.userName}>
                  {user.firstName} {user.lastName}
                </div>
                <div className={styles.userEmail}>{user.email}</div>
              </div>
            )}
          </div>
          <button onClick={onClose} className={styles.closeButton} aria-label="Close menu">
            ✕
          </button>
        </div>

        <nav className={styles.menuNav}>
          <button onClick={onNavigateTasks} className={styles.menuItem}>
            <span className={styles.menuIcon} aria-hidden="true">
              <TargetIcon size={24} />
            </span>
            <span className={styles.menuLabel}>Action Plan</span>
          </button>

          <button onClick={onNavigateBracelet} className={styles.menuItem} data-tour="bracelet-menu-item">
            <span className={styles.menuIcon} aria-hidden="true">
              <SparkleIcon size={24} />
            </span>
            <span className={styles.menuLabel}>Beads</span>
          </button>

          <button onClick={onNavigateArticles} className={styles.menuItem}>
            <span className={styles.menuIcon} aria-hidden="true">
              <BookIcon size={24} />
            </span>
            <span className={styles.menuLabel}>Learning Resources</span>
          </button>

          {/* News — HIDDEN (not in use right now). Screen and routes remain
              intact; flip SHOW_NEWS to re-enable. */}
          {SHOW_NEWS && (
            <button onClick={onNavigateNews} className={styles.menuItem}>
              <span className={styles.menuIcon} aria-hidden="true">
                <NewsIcon size={24} />
              </span>
              <span className={styles.menuLabel}>All News</span>
            </button>
          )}

          {/* Money Dates — HIDDEN (deprioritized). Screen, routes and jobs
              remain intact; flip SHOW_MONEY_DATES to re-enable. */}
          {SHOW_MONEY_DATES && (
            <button onClick={onNavigateMoneyDates} className={styles.menuItem}>
              <span className={styles.menuIcon} aria-hidden="true">
                <UsersIcon size={24} />
              </span>
              <span className={styles.menuLabel}>Money Dates</span>
            </button>
          )}

          {/* Circles / Sophia Together — App.jsx omits both handlers when the
              feature is switched off server-side (ENABLE_CIRCLES=false). */}
          {onNavigateCircles && (
            <button onClick={onNavigateCircles} className={styles.menuItem}>
              <span className={styles.menuIcon} aria-hidden="true">
                <CirclesIcon size={24} />
              </span>
              <span className={styles.menuLabel}>Circles</span>
            </button>
          )}

          {onNavigateFamily && (
            <button onClick={onNavigateFamily} className={styles.menuItem}>
              <span className={styles.menuIcon} aria-hidden="true">
                <FamilyIcon size={24} />
              </span>
              <span className={styles.menuLabel}>Sophia Together</span>
            </button>
          )}

          {/* Coach Dashboard - only shown if user is a coach */}
          {user?.isCoach && onNavigateCoachDashboard && (
            <button onClick={onNavigateCoachDashboard} className={styles.menuItem}>
              <span className={styles.menuIcon} aria-hidden="true">
                <BriefcaseIcon size={24} />
              </span>
              <span className={styles.menuLabel}>Coach Dashboard</span>
            </button>
          )}

          {/* Organization Dashboard - only shown if user is org admin */}
          {user?.adminOfOrganizations && user.adminOfOrganizations.length > 0 && onNavigateOrgDashboard && (
            <button onClick={onNavigateOrgDashboard} className={styles.menuItem}>
              <span className={styles.menuIcon} aria-hidden="true">
                <UsersIcon size={24} />
              </span>
              <span className={styles.menuLabel}>Organization Dashboard</span>
            </button>
          )}

          {/* Community Manager Hub - admins of a Community-type organization,
              plus anyone who can see the Dev Dashboard. The flag only toggles
              the menu item; /api/community-hub enforces the rule. */}
          {user?.canAccessCommunityHub && onNavigateCommunityHub && (
            <button onClick={onNavigateCommunityHub} className={styles.menuItem}>
              <span className={styles.menuIcon} aria-hidden="true">
                <MegaphoneIcon size={24} />
              </span>
              <span className={styles.menuLabel}>Community Manager Hub</span>
            </button>
          )}

          {/* Security/Dev Dashboard - shown in local dev, or in prod to users
              on the SECURITY_ALERT_EMAIL allowlist. This only toggles the menu
              item; server-side /api/dev enforces the real authorization. */}
          {(process.env.NODE_ENV === 'development' || user?.isSecurityAdmin) && onNavigateDevDashboard && (
            <button onClick={onNavigateDevDashboard} className={styles.menuItem}>
              <span className={styles.menuIcon} aria-hidden="true">
                <BugIcon size={24} />
              </span>
              <span className={styles.menuLabel}>Dev Dashboard</span>
            </button>
          )}

          <div className={styles.menuDivider}></div>

          <button onClick={onFeedback} className={styles.menuItem}>
            <span className={styles.menuIcon} aria-hidden="true">
              <MessageCircleIcon size={24} />
            </span>
            <span className={styles.menuLabel}>Send Feedback</span>
          </button>

          {/* Notification Settings */}
          {'Notification' in window && (
            <button
              onClick={handleEnableNotifications}
              className={styles.menuItem}
              data-tour="enable-notifications"
            >
              <span className={styles.menuIcon} aria-hidden="true">
                <BellIcon size={24} />
              </span>
              <span className={styles.menuLabel}>
                {notificationPermission === 'granted' ? (
                  'Notifications Enabled ✓'
                ) : notificationPermission === 'denied' ? (
                  <>
                    Notifications Blocked
                    <span className={styles.menuLabelHelper}>(Tap for Help)</span>
                  </>
                ) : (
                  'Enable Notifications'
                )}
              </span>
            </button>
          )}

          {/* PWA Install */}
          {(isInstallable || isIOS) && (
            <button
              onClick={handleInstallPWA}
              className={styles.menuItem}
              data-tour="install-app"
            >
              <span className={styles.menuIcon} aria-hidden="true">
                <DownloadIcon size={24} />
              </span>
              <span className={styles.menuLabel}>
                Install App
              </span>
            </button>
          )}

          <button onClick={onStartTour} className={styles.menuItem}>
            <span className={styles.menuIcon} aria-hidden="true">
              <HelpCircleIcon size={24} />
            </span>
            <span className={styles.menuLabel}>Tutorial</span>
          </button>

          <div className={styles.menuDivider}></div>

          <button onClick={onNavigateSettings} className={styles.menuItem}>
            <span className={styles.menuIcon} aria-hidden="true">
              <UserIcon size={24} />
            </span>
            <span className={styles.menuLabel}>Settings</span>
          </button>
        </nav>
      </div>
    </div>
    </>
  );
}

export default HamburgerMenu;
